import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TextField,
  IconButton,
  InputAdornment,
  TablePagination,
  Grid,
  Box,
} from "@mui/material";
import Chip from "@mui/material/Chip";
import { useNavigate } from "react-router-dom";
import Collapse from "@mui/material/Collapse";
import { useDispatch, useSelector } from "react-redux";
import Fab from "@mui/material/Fab";
import LinearProgress from "@mui/material/LinearProgress";
import {
  getSubscriptionsAction,
  setOffset,
  cleanSubscriptionAction,
  getSubscriptionAction,
  setQuery,
  setTypeFilter,
} from "../../redux/actions/subscription";
import strings from "../../i18n/language";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import WarningIcon from "@mui/icons-material/Warning";
import DoneIcon from "@mui/icons-material/Done";
import SubscriptionDelete from "../../dialog/SubscriptionDelete";
import SubscriptionPatients from "../../dialog/SubscriptionPatients";
import SubscriptionPayments from "../../dialog/SubscriptionPayments";
import SubscriptionArchive from "../../dialog/SubscriptionArchive";
import Typography from "@mui/material/Typography";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";
import InfoIcon from "@mui/icons-material/Info";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";

const SubscriptionsList = () => {
  const subscriptions = useSelector(
    (state) => state.subscription.subscriptions
  );
  const [subscriptionDeleteId, setSubscriprionDeleteId] = useState(null);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [openModalPatient, setOpenModalPatients] = useState(false);
  const [openModalPayments, setOpenModalPayments] = useState(false);
  const [openModalArchive, setOpenModalArchive] = useState(false);
  const [subscriptionToArchive, setSubscriptionToArchive] = useState(null);
  const dispatch = useDispatch();
  const subscriptionsRef = useRef([]);
  const navigate = useNavigate();
  const total = useSelector((state) => state.subscription.total);
  const limit = useSelector((state) => state.subscription.limit);

  const typeFilter = useSelector((state) => state.subscription.typeFilter);
  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const user = useSelector((state) => state.user.user);

  useEffect(() => {
    setSearching(true);
    setPage(0);
    const timeoutId = setTimeout(() => {
      setSearching(false);
      dispatch(setQuery(searchQuery));
    }, 1500);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, dispatch]);

  const isLoadingGetSubscriptions = useSelector(
    (state) => state.subscription.isLoadingGetSubscriptions
  );

  const Row = ({
    subscription,
    setSubscriprionDeleteId,
    setOpenModalDelete,
  }) => {
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    return (
      <>
        <TableRow
          sx={{
            backgroundColor: subscription.archive
              ? theme.palette.action.disabledBackground
              : "inherit",
            opacity: subscription.archive ? 0.5 : 1,
          }}
        >
          <TableCell>
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          <TableCell component="th" scope="row">
            {(() => {
              if (!subscription.validity)
                return (
                  <WarningIcon style={{ color: theme.palette.warning.main }} />
                );
              const isBeforeNow = dayjs(subscription.validity).isBefore(
                dayjs()
              );
              return isBeforeNow ? (
                <WarningIcon style={{ color: theme.palette.warning.main }} />
              ) : (
                <DoneIcon style={{ color: theme.palette.success.main }} />
              );
            })()}
            ({subscription.id})
          </TableCell>
          <TableCell component="th" scope="row">
            {(() => {
              switch (subscription.type) {
                case "individual":
                  return <PersonIcon style={{ opacity: 0.6 }} />;
                case "family":
                  return (
                    <IconButton
                      aria-label="patients"
                      onClick={() => {
                        setOpenModalPatients(true);
                        dispatch(getSubscriptionAction(subscription.id));
                      }}
                    >
                      <GroupsIcon />
                    </IconButton>
                  );
                case "company":
                  return (
                    <IconButton
                      aria-label="patients"
                      onClick={() => {
                        setOpenModalPatients(true);
                        dispatch(getSubscriptionAction(subscription.id));
                      }}
                    >
                      <BusinessIcon />
                    </IconButton>
                  );
                default:
                  return "Other";
              }
            })()}
          </TableCell>
          <TableCell>
            {(() => {
              switch (subscription.type) {
                case "individual":
                  return subscription.PatientSubscriptions?.[0]?.Patient
                    ?.displayName;
                case "family":
                  return subscription.PatientSubscriptions?.[0]?.Patient
                    ?.displayName;
                case "company":
                  return subscription.Company?.name;
                default:
                  return "Other";
              }
            })()}
          </TableCell>

          <TableCell>{dayjs(subscription.start).format("L")}</TableCell>
          <TableCell>
            {subscription.validity
              ? dayjs(subscription.validity).format("L")
              : strings.LABEL_MISSING}
          </TableCell>
          <TableCell
            sx={{
              whiteSpace: "nowrap",
            }}
          >
            <IconButton
              aria-label="payments"
              onClick={() => {
                setOpenModalPayments(true);
                dispatch(getSubscriptionAction(subscription.id));
              }}
            >
              <AttachMoneyIcon />
            </IconButton>
            {user.type !== "viewer" && (
              <IconButton
                aria-label="delete"
                onClick={() => {
                  setSubscriprionDeleteId(subscription.id);
                  setOpenModalDelete(true);
                }}
              >
                <DeleteIcon />
              </IconButton>
            )}
            {user.type !== "viewer" && (
              <IconButton
                aria-label="edit"
                onClick={() => {
                  dispatch(getSubscriptionAction(subscription.id));
                  navigate("/subscriptions/edit");
                }}
              >
                <EditIcon />
              </IconButton>
            )}
            {user.type !== "viewer" && (
              <IconButton
                aria-label={subscription.archive ? "unarchive" : "archive"}
                onClick={() => {
                  setSubscriptionToArchive(subscription);
                  setOpenModalArchive(true);
                }}
              >
                {subscription.archive ? <UnarchiveIcon /> : <ArchiveIcon />}
              </IconButton>
            )}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell
            style={{ paddingBottom: 0, paddingTop: 0, borderBottom: "none" }}
            colSpan={6}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>
                <Typography variant="h6" gutterBottom component="div">
                  {strings.MESSAGE_LAST_PAYMENTS}
                </Typography>
                <Table size="small" aria-label="purchases">
                  <TableHead>
                    <TableRow>
                      <TableCell>{strings.LABEL_DATE}</TableCell>
                      <TableCell>{strings.LABEL_TYPE}</TableCell>
                      <TableCell>{strings.LABEL_CURRENCY}</TableCell>
                      <TableCell align="right">
                        {strings.LABEL_AMOUNT}
                      </TableCell>
                      <TableCell>{strings.LABEL_COMMENT}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subscription.Payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>
                          {dayjs(payment.date).format("L")}
                        </TableCell>
                        <TableCell>
                          {payment.type === "efectivo"
                            ? strings.LABEL_CASH
                            : payment.type === "transferencia"
                            ? strings.LABEL_TRANSFER
                            : payment.type === "pago_movil"
                            ? strings.LABEL_MOBILE_PAYMENT
                            : payment.type}
                        </TableCell>
                        <TableCell>{payment.currency}</TableCell>
                        <TableCell align="right">{payment.amount}</TableCell>
                        <TableCell>{payment.comment}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  };

  useEffect(() => {
    dispatch(getSubscriptionsAction());
  }, [dispatch]);

  useEffect(() => {
    if (subscriptions) subscriptionsRef.current = subscriptions;
  }, [subscriptions]);

  useEffect(() => {
    setSearching(true);
    const timeoutId = setTimeout(() => {
      setSearching(false);
    }, 1500);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, dispatch]);
  return (
    <>
      <Paper sx={{ p: 2, width: "80%" }}>
        {(searching || isLoadingGetSubscriptions) && (
          <LinearProgress
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              width: "100%",
            }}
          />
        )}
        <TextField
          fullWidth
          variant="outlined"
          placeholder={strings.LABEL_SEARCH}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            sx: { borderRadius: 25 }, // Rounded corners for input field
          }}
          sx={{ mb: 2 }}
        />
        <Box display="flex" gap={1} sx={{ mb: 2 }}>
          {[
            { value: "individual", label: "Individual" },
            { value: "family", label: "Grupo Familiar" },
            { value: "company", label: "Empresa" },
          ].map((item) => (
            <Chip
              key={item.value}
              label={item.label}
              color={typeFilter === item.value ? "primary" : "default"}
              variant={typeFilter === item.value ? "filled" : "outlined"}
              onClick={() => {
                setPage(0);
                dispatch(setTypeFilter(typeFilter === item.value ? null : item.value));
              }}
              clickable
            />
          ))}
        </Box>
        {searchQuery && (
          <Grid item xs={8} mt={1} display="flex" alignItems="center">
            <InfoIcon style={{ marginRight: 8 }} />
            <Typography>
              {strings.MESSAGE_SEARCH_PATIENT_IN_SUSCRIPTION}
            </Typography>
          </Grid>
        )}
        {subscriptions && (
          <TableContainer>
            <Table aria-label="Subscriptions table">
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>{strings.LABEL_STATUS}</TableCell>
                  <TableCell colSpan={2}>{strings.LABEL_TYPE}</TableCell>
                  <TableCell>{strings.LABEL_START}</TableCell>
                  <TableCell>{strings.LABEL_VALIDITY}</TableCell>
                  <TableCell>{strings.LABEL_ACTION}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subscriptions.map((subscription) => (
                  <Row
                    key={subscription.id}
                    subscription={subscription}
                    setSubscriprionDeleteId={setSubscriprionDeleteId}
                    setOpenModalDelete={setOpenModalDelete}
                  ></Row>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[limit]}
              count={total}
              rowsPerPage={limit}
              page={page}
              onPageChange={(event, newPage) => {
                dispatch(setOffset(limit * newPage));
                setPage(newPage);
              }}
              component="div"
            />
          </TableContainer>
        )}
      </Paper>
      {user.type !== "viewer" && (
        <Fab
          style={{ alignSelf: "flex-end", marginLeft: 10 }}
          color="primary"
          aria-label="add"
          onClick={() => {
            dispatch(cleanSubscriptionAction());
            navigate("/subscriptions/edit");
          }}
        >
          <AddIcon />
        </Fab>
      )}
      <SubscriptionDelete
        open={openModalDelete}
        setOpen={setOpenModalDelete}
        patientDeleteId={subscriptionDeleteId}
      ></SubscriptionDelete>
      <SubscriptionPatients
        open={openModalPatient}
        setOpen={setOpenModalPatients}
      ></SubscriptionPatients>
      <SubscriptionPayments
        open={openModalPayments}
        setOpen={setOpenModalPayments}
      ></SubscriptionPayments>
      <SubscriptionArchive
        open={openModalArchive}
        setOpen={setOpenModalArchive}
        subscription={subscriptionToArchive}
      ></SubscriptionArchive>
    </>
  );
};

export default SubscriptionsList;
